import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Box, Chip, Divider, Stack, Tab, Tabs, Typography} from "@mui/material";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {CorrespondenceMessageService} from "crqlar-react-common/dist/services/correspondence/correspondenceMessage.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import LogDetails from "crqlar-react-common/dist/components/logs/LogDetails";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

const CorrespondenceMessageDetails = () => {

    useTrackScreenVisit('system correspondence message details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'userLogs'], 'basic');
    const setActiveTab = (newTab: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', newTab);
        setSearchParams(nextSearchParams);
    };

    const messageData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return CorrespondenceMessageService.getMessageById(id)
            } else {
                return {}
            }
        },
        create: CorrespondenceMessageService.createMessage,
        update: CorrespondenceMessageService.updateMessage,
        delete: (id) => CorrespondenceMessageService.deleteMessage({id: [id]}),
        onDeleted: () => navigate('/system/correspondenceMessages', {replace: true}),
        onSaved: (message, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/correspondenceMessages/${message?.id}`, {replace: true})
            }
        },
    }, id)

    const message = messageData.item

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}
    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    const formatContact = (contact: typeof message.messageFrom) => {
        const address = contact?.email || contact?.phone
        const name = contact?.fullName || [contact?.firstName, contact?.lastName].filter(Boolean).join(' ')
        const value = [name, address].filter(Boolean).join(' - ') || '-'

        return contact?.sentByOrganization ? `${value} (${t('Organization')})` : value
    }

    const textValue = (value?: React.ReactNode) => (
        <Typography variant={'body1'} sx={{overflowWrap: 'anywhere'}}>
            {value || '-'}
        </Typography>
    )

    const timestampRows = [
        {label: t('Scheduled'), value: message.timestamps?.scheduled},
        {label: t('Sending'), value: message.timestamps?.sending},
        {label: t('Sent'), value: message.timestamps?.sent},
        {label: t('Delivered'), value: message.timestamps?.delivered},
        {label: t('Read'), value: message.timestamps?.read},
        {label: t('Failed'), value: message.timestamps?.failed, color: 'error.main'},
    ].filter(row => !!row.value)

    const hasContent = !!message.simplifiedContent || !!message.content
    const hasUsageStats = !!message.usageStats?.smsNumSegments || !!message.usageStats?.price

    const headerSection = (
        <SettingsEntityHeader
            title={message.subject || (message.id ? `#${message.id}` : undefined)}
            data={messageData}
            back={() => navigate('/system/correspondenceMessages')}
            restrictEdit
        />
    )

    const tabsSection = message.id ? (
        <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            aria-label="correspondence tabs"
            sx={{
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Tab label={t('Basic info')} value={'basic'}/>
            <Tab label={t('User logs')} value={'userLogs'}/>
        </Tabs>
    ) : null

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property title={t('Subject')}>
                    {textValue(message.subject)}
                </Property>

                <Property title={t('From')}>
                    {textValue(formatContact(message.messageFrom))}
                </Property>

                <Property title={t('To')}>
                    {textValue(formatContact(message.messageTo))}
                </Property>

                <Property title={t('Channel')}>
                    <Stack direction={'row'} width={'100%'}>
                        <Chip
                            label={message.channelType ? t(`Channel ${message.channelType}`) : '-'}
                            size={'small'}
                            color={'primary'}
                            variant={'outlined'}
                        />
                    </Stack>
                </Property>

                <Property title={t('Status')}>
                    <Stack direction={'row'} width={'100%'}>
                        <Chip
                            label={message.status ? t(`Message status ${message.status}`) : '-'}
                            size={'small'}
                            color={message.status === 'FAILED' ? 'error' : 'success'}
                            variant={'outlined'}
                        />
                    </Stack>
                </Property>

                <Property title={t('Direction')}>
                    <Stack direction={'row'} width={'100%'}>
                        <Chip
                            label={message.outbound ? t('Outbound') : t('Inbound')}
                            size={'small'}
                            variant={'outlined'}
                        />
                    </Stack>
                </Property>

                {!!message.labels?.length && (
                    <Property title={t('Labels')} labelAlignment={'top'}>
                        <Stack direction={'row'} gap={1} flexWrap={'wrap'} width={'100%'}>
                            {message.labels.map((label, index) => (
                                <Chip key={`${label}-${index}`} label={label} size={'small'}/>
                            ))}
                        </Stack>
                    </Property>
                )}

                {!!message.attachments?.length && (
                    <Property title={t('Attachments')} labelAlignment={'top'}>
                        <Stack gap={0.5} width={'100%'}>
                            {message.attachments.map((attachment, index) => (
                                <Typography key={`${attachment.fileName}-${index}`} variant={'body2'}>
                                    {[attachment.fileName, attachment.type && `(${attachment.type})`].filter(Boolean).join(' ')}
                                </Typography>
                            ))}
                        </Stack>
                    </Property>
                )}
            </Stack>
        </Stack>
    )

    const contentSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Content')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                {message.simplifiedContent && (
                    <Property title={t('Simplified content')} labelAlignment={'top'}>
                        <Typography variant={'body2'} sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                            {message.simplifiedContent}
                        </Typography>
                    </Property>
                )}

                {message.content && (
                    <Property title={t('Full content')} labelAlignment={'top'}>
                        <Box
                            sx={{
                                width: '100%',
                                p: 1.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                maxHeight: 500,
                                overflow: 'auto',
                                overflowWrap: 'anywhere',
                            }}
                            dangerouslySetInnerHTML={{__html: message.content}}
                        />
                    </Property>
                )}

                {!hasContent && (
                    <Typography variant={'body1'} color={'text.secondary'}>
                        {t('No content available')}
                    </Typography>
                )}
            </Stack>
        </Stack>
    )

    const timestampsSection = timestampRows.length ? (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Timestamps')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                {timestampRows.map((row) => (
                    <Property key={row.label} title={row.label}>
                        <Typography variant={'body1'} color={row.color}>
                            {formatFullDateTime(row.value!)}
                        </Typography>
                    </Property>
                ))}
            </Stack>
        </Stack>
    ) : null

    const usageStatsSection = hasUsageStats ? (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Usage statistics')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                {!!message.usageStats?.smsNumSegments && (
                    <Property title={t('SMS segments')}>
                        {textValue(message.usageStats.smsNumSegments)}
                    </Property>
                )}

                {!!message.usageStats?.price && (
                    <Property title={t('Price')}>
                        {textValue(`${message.usageStats.price} ${message.usageStats.priceCurrencyCode || ''}`.trim())}
                    </Property>
                )}
            </Stack>
        </Stack>
    ) : null

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete message')}
                description={t('This permanently deletes the message. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={messageData.saving}
                disabled={readOnly}
                onDelete={() => messageData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {message.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const basicTabContent = (
        <Stack gap={{xs: 5, sm: 6}}>
            {basicInfoSection}
            <Divider sx={sectionDividerSx}/>
            {contentSection}

            {timestampsSection && (
                <>
                    <Divider sx={sectionDividerSx}/>
                    {timestampsSection}
                </>
            )}

            {usageStatsSection && (
                <>
                    <Divider sx={sectionDividerSx}/>
                    {usageStatsSection}
                </>
            )}

            {!!message.id && (
                <>
                    <Divider sx={sectionDividerSx}/>
                    {dangerZoneSection}
                </>
            )}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {tabsSection}

                {activeTab === 'basic' && basicTabContent}

                {activeTab === 'userLogs' && (
                    <LogDetails entityId={id}/>
                )}
            </Stack>
        </Box>
    );
}

export default CorrespondenceMessageDetails;
